import { ApiErrorData, ApiResponse } from 'models/ApiResponse';
import {
  AcceptLeadsResponse,
  AssociateLeadsResponse,
  AssociationsResponse,
  AvailableLeadsParams,
  AvailableLeadsResponse,
  CreateRequirementInput,
  RejectLeadsResponse,
  RemoveLeadsResponse,
  RequirementDetail,
  RequirementsListParams,
  RequirementsListResponse,
  TouchLatamTask,
  UpdateRequirementInput,
  UpdateStatusInput,
  UpdateStatusResponse,
} from 'models/Requirement';

import { FunctionsHelper } from 'utils/FunctionsHelper';
import MainBackendAxios from 'utils/MainBackendAxios';

const BASE = '/requisition';

export const RequirementService = {
  getTasks: async (
    country: string
  ): Promise<ApiResponse<TouchLatamTask[] | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/tasks`, {
        params: { country },
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getRequirements: async (
    params: RequirementsListParams
  ): Promise<ApiResponse<RequirementsListResponse | ApiErrorData>> => {
    try {
      const queryParams: Record<string, any> = {
        country: params.country,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        sort: params.sort ?? 'createdAt',
        dir: params.dir ?? 'desc',
      };

      if (params.status) queryParams.status = params.status;
      if (params.term) queryParams.term = params.term;
      if (params.taskId !== undefined) queryParams.taskId = params.taskId;

      const response = await MainBackendAxios.get(`${BASE}`, {
        params: queryParams,
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  createRequirement: async (
    body: CreateRequirementInput
  ): Promise<ApiResponse<RequirementDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(`${BASE}`, body);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  // PATCH /:id — edición parcial de metadata (name, referentialCapacity,
  // description, leadAssociationDeadline, taskId). La respuesta NO incluye
  // `task` ni `associatedCount` (exclusivos del detalle GET /:id), por eso el
  // caller recarga el detalle tras guardar.
  updateRequirement: async (
    id: string,
    body: UpdateRequirementInput
  ): Promise<ApiResponse<RequirementDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(`${BASE}/${id}`, body);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getRequirementById: async (
    id: string
  ): Promise<ApiResponse<RequirementDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/${id}`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getAvailableLeads: async (
    id: string,
    params: AvailableLeadsParams
  ): Promise<ApiResponse<AvailableLeadsResponse | ApiErrorData>> => {
    try {
      const queryParams: Record<string, any> = {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        sort: params.sort ?? 'fullName',
        dir: params.dir ?? 'asc',
      };

      if (params.term) queryParams.term = params.term;
      if (params.cityIds && params.cityIds.length > 0) {
        queryParams.cityIds = params.cityIds.join(',');
      }
      if (params.minAge !== undefined) queryParams.minAge = params.minAge;
      if (params.maxAge !== undefined) queryParams.maxAge = params.maxAge;
      if (params.scoreAptitude && params.scoreAptitude.length > 0) {
        queryParams.scoreAptitude = params.scoreAptitude.join(',');
      }
      if (params.scoreProfile && params.scoreProfile.length > 0) {
        queryParams.scoreProfile = params.scoreProfile.join(',');
      }

      const response = await MainBackendAxios.get(
        `${BASE}/${id}/leads/available`,
        {
          params: queryParams,
        }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getAssociations: async (
    id: string
  ): Promise<ApiResponse<AssociationsResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/${id}/associations`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  associateLeads: async (
    id: string,
    userIds: string[]
  ): Promise<ApiResponse<AssociateLeadsResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `${BASE}/${id}/associations`,
        { userIds }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  removeLeads: async (
    id: string,
    userIds: string[]
  ): Promise<ApiResponse<RemoveLeadsResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.delete(
        `${BASE}/${id}/associations`,
        { data: { userIds } }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  acceptLeads: async (
    id: string,
    userIds: string[]
  ): Promise<ApiResponse<AcceptLeadsResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(
        `${BASE}/${id}/associations/accept`,
        { userIds }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  rejectLeads: async (
    id: string,
    userIds: string[]
  ): Promise<ApiResponse<RejectLeadsResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(
        `${BASE}/${id}/associations/reject`,
        { userIds }
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateStatus: async (
    id: string,
    body: UpdateStatusInput
  ): Promise<ApiResponse<UpdateStatusResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(
        `${BASE}/${id}/status`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
};
